import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCompany } from '../context/CompanyContext';
import { useUser } from '../context/UserContext';
import { useAd } from '../context/AdContext';
import { flightsAPI, ticketsAPI } from '../api';
import { useFetch } from '../hooks/useFetch';
import { Card, Button, Badge, LoadingView, ErrorView, EmptyView, Modal, Toast, FieldLabel, FieldInput } from '../components/UI';
import { fmt } from '../utils/formatters';
import { statusColor } from '../theme/colors';

function FlightCard({ fl, onPress }) {
    const sc = statusColor(fl.status);
    return (
        <Card onClick={onPress} style={{ marginBottom: 10 }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', marginBottom: 8 }}>
                <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
                        <span style={{
                            fontFamily: 'var(--font-display)', fontSize: 22, fontWeight: 800,
                            color: 'var(--text-primary)'
                        }}>{fl.origin}</span>
                        <span style={{ color: 'var(--brand-400)', fontSize: 18 }}>✈</span>
                        <span style={{
                            fontFamily: 'var(--font-display)', fontSize: 22, fontWeight: 800,
                            color: 'var(--text-primary)'
                        }}>{fl.destination}</span>
                    </div>
                    <p style={{ color: 'var(--text-muted)', fontSize: 12 }}>
                        {fl.airline} {fl.flightNumber}
                        {fl.aircraft ? ` · ${fl.aircraft}` : ''}
                        {fl.stops === 0 ? ' · Non-stop' : fl.stops === 1 ? ' · 1 Stop' : ` · ${fl.stops} Stops`}
                    </p>
                </div>
                <Badge label={fl.status || 'active'} bgColor={sc.bg} color={sc.text} borderColor={sc.border} />
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                <span style={{ color: 'var(--text-secondary)', fontSize: 13 }}>
                    {fmt.time(fl.departureTime)} · {fmt.date(fl.departureDate)}
                </span>
                {fl.duration && <span style={{ color: 'var(--text-muted)', fontSize: 13 }}>⏱ {fl.duration}</span>}
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 8 }}>
                {Object.entries(fl.availabilityByClass || {}).map(([cls, info]) => (
                    <span key={cls} style={{
                        backgroundColor: 'var(--bg-surface)', borderRadius: 'var(--radius-full)',
                        padding: '3px 10px', fontSize: 12
                    }}>
                        <span style={{ color: 'var(--text-muted)', textTransform: 'capitalize' }}>{cls} </span>
                        <span style={{ color: 'var(--brand-300)', fontWeight: 600 }}>{fmt.money(info.minPrice, fl.currency?.toUpperCase() || 'USD')}</span>
                    </span>
                ))}
            </div>
            <div style={{ borderTop: '1px solid var(--border)', paddingTop: 8 }}>
                <span style={{ color: 'var(--brand-300)', fontSize: 12, fontWeight: 600 }}>
                    {fl.availableSeats || fl.totalAvailableSeats || 0} Seats Available
                </span>
            </div>
        </Card>
    );
}

function SeatModal({ visible, flight, onClose, onConfirm }) {
    const { profile } = useUser();
    const [selClass, setSelClass] = useState(null);
    const [selSeat, setSelSeat] = useState(null);
    const [name, setName] = useState(profile.name || '');
    const [email, setEmail] = useState(profile.email || '');
    const [loading, setLoading] = useState(false);

    React.useEffect(() => {
        if (visible) { setSelClass(null); setSelSeat(null); setName(profile.name || ''); setEmail(profile.email || ''); setLoading(false); }
    }, [visible]);

    const availSeats = (flight?.seats || []).filter(s => s.available !== false);
    const classSeatMap = availSeats.reduce((acc, s) => {
        const c = s.class || 'economy';
        if (!acc[c]) acc[c] = [];
        acc[c].push(s);
        return acc;
    }, {});
    const classesToShow = Object.keys(flight?.availabilityByClass || {});
    const seatsForClass = selClass ? (classSeatMap[selClass] || []) : [];

    const handleBook = async () => {
        if (!selSeat) { Toast.show({ type: 'error', text1: 'Select a Seat' }); return; }
        setLoading(true);
        await onConfirm({ flight, seat: selSeat, name, email });
        setLoading(false);
    };

    return (
        <Modal visible={visible} onClose={onClose} title={flight?.flightNumber || 'Book Flight'}>
            <p style={{ color: 'var(--text-muted)', fontSize: 13, marginTop: -8, marginBottom: 12 }}>
                {flight?.origin} → {flight?.destination} · {fmt.date(flight?.departureDate)}
                {flight?.aircraft && <span> · ✈️ {flight.aircraft}</span>}
            </p>

            <FieldLabel>Choose Class</FieldLabel>
            <div style={{ display: 'flex', gap: 8, marginTop: 4, marginBottom: 12 }}>
                {classesToShow.map(cls => {
                    const info = flight.availabilityByClass[cls];
                    const isSel = selClass === cls;
                    return (
                        <button key={cls} onClick={() => { setSelClass(cls); setSelSeat(null); }} style={{
                            flex: 1, padding: '10px 8px', borderRadius: 10, border: '1px solid',
                            borderColor: isSel ? 'var(--brand-400)' : 'var(--border)',
                            backgroundColor: isSel ? 'var(--brand-600)' : 'var(--bg-input)',
                            cursor: 'pointer', textAlign: 'center',
                        }}>
                            <p style={{ color: isSel ? '#fff' : 'var(--text-primary)', fontSize: 13, fontWeight: 600, textTransform: 'capitalize', marginBottom: 2 }}>
                                {cls === 'economy' ? '💺' : cls === 'business' ? '👔' : '⭐'} {cls}
                            </p>
                            <p style={{ color: isSel ? 'var(--brand-100)' : 'var(--brand-300)', fontSize: 11 }}>
                                from {fmt.money(info.minPrice, 'USD')}
                            </p>
                            <p style={{ color: isSel ? 'var(--brand-200)' : 'var(--text-muted)', fontSize: 11 }}>{info.count} left</p>
                        </button>
                    );
                })}
            </div>

            {selClass && seatsForClass.length > 0 && (
                <>
                    <FieldLabel>Choose Seat</FieldLabel>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 4, marginBottom: 8 }}>
                        {seatsForClass.slice(0, 20).map(s => {
                            const isSel = selSeat?.seatId === s.seatId;
                            return (
                                <button key={s.seatId} onClick={() => setSelSeat(s)} style={{
                                    padding: '6px 10px', borderRadius: 6, border: '1px solid var(--border)',
                                    backgroundColor: isSel ? 'var(--brand-500)' : 'var(--bg-surface)',
                                    cursor: 'pointer', minWidth: 56, textAlign: 'center',
                                }}>
                                    <p style={{ color: isSel ? '#fff' : 'var(--brand-200)', fontSize: 12, fontWeight: 700 }}>
                                        {s.seat || s.seatId}
                                    </p>
                                    <p style={{ color: isSel ? 'var(--brand-100)' : 'var(--text-muted)', fontSize: 10 }}>
                                        {fmt.money(s.price, flight?.currency?.toUpperCase() || 'USD')}
                                    </p>
                                </button>
                            );
                        })}
                    </div>
                </>
            )}

            <FieldLabel>Passenger Name</FieldLabel>
            <FieldInput value={name} onChange={e => setName(e.target.value)} placeholder="John Smith" />
            <FieldLabel>Email (For Ticket)</FieldLabel>
            <FieldInput value={email} onChange={e => setEmail(e.target.value)} placeholder="you@yourdomain.com" type="email" />

            {selSeat && (
                <div style={{
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    borderTop: '1px solid var(--border)', marginTop: 16, paddingTop: 16
                }}>
                    <span style={{ color: 'var(--text-muted)', fontSize: 14 }}>Total</span>
                    <span style={{ color: 'var(--brand-300)', fontSize: 18, fontWeight: 700, fontFamily: 'var(--font-mono)' }}>
                        {fmt.money(selSeat.price, flight?.currency?.toUpperCase() || 'USD')}
                    </span>
                </div>
            )}

            <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
                <Button label="Cancel" variant="outline" onClick={onClose} style={{ flex: 1 }} />
                <Button label="Book Seat" onClick={handleBook} loading={loading} disabled={!selSeat} style={{ flex: 1 }} />
            </div>
        </Modal>
    );
}

export default function FlightsScreen() {
    const navigate = useNavigate();
    const { company } = useCompany();
    const { profile, updateProfile } = useUser();
    const { maybeShowAd } = useAd();
    const [seatTarget, setSeatTarget] = useState(null);

    const { data, loading, error, refetch } = useFetch(
        () => flightsAPI.list(company?.companyId, { limit: 20, includeAll: 'false' }),
        [company?.companyId]
    );
    const flights = data?.flights || [];

    const handleBookSeat = async ({ flight, seat, name, email }) => {
        try {
            if (name) updateProfile({ name });
            if (email) updateProfile({ email });
            const { data: d } = await ticketsAPI.issue({
                type: 'flight', flightId: flight.flightId,
                companyId: flight.companyId || company.companyId,
                seatId: seat.seatId, seatClass: seat.class || 'economy',
                callerName: name, callerEmail: email, callerPhone: profile.phone || null,
                paymentMethod: 'link', calledFrom: 'web',
            });
            setSeatTarget(null);
            navigate('/booking-confirm', {
                state: {
                    type: 'ticket', id: d.ticketId,
                    data: { flight: `${flight.flightNumber} ${flight.origin}→${flight.destination}`, seat: seat.seat || seat.seatId, class: seat.class },
                    confirmationNumber: d.confirmationNumber,
                    paymentUrl: d.payment?.checkoutUrl || d.checkoutUrl || null,
                    qrUrl: d.qrUrl || null
                },
            });
        } catch (err) {
            Toast.show({ type: 'error', text1: err.response?.data?.error || 'Booking Failed' });
        }
    };

    if (loading) return <LoadingView label="Loading Flights…" />;
    if (error) return <ErrorView message={error} onRetry={refetch} />;

    return (
        <div style={{ padding: 16 }}>
            <h1 style={{
                fontFamily: 'var(--font-display)', fontSize: 22, fontWeight: 800,
                marginBottom: 16, color: 'var(--text-primary)'
            }}>Available Flights</h1>
            {flights.length === 0
                ? <EmptyView emoji="✈️" title="No Flights Available" subtitle="Check back soon!" />
                : flights.map(fl => (
                    <FlightCard key={fl.flightId} fl={fl}
                        onPress={() => { maybeShowAd('tickets'); setSeatTarget(fl); }} />
                ))
            }
            <SeatModal visible={!!seatTarget} flight={seatTarget}
                onClose={() => setSeatTarget(null)} onConfirm={handleBookSeat} />
        </div>
    );
}
